// ===================================================
// Entity Platform — Supabase Queries
// ===================================================
// CRUD for global fields, entity types, notes, and relations.
// Cross-entity search: find a phone number across all entity types.

import { supabase } from '@/lib/supabaseClient';
import { resolveRecordWriteContext } from '@/lib/supabase/recordWriteContext';
import type {
  GlobalField, GlobalFieldInsert,
  EntityType, EntityTypeInsert,
  EntityConnection, EntityConnectionInsert,
  FieldGroup, FieldGroupInsert,
  NoteRecord, NoteRelation,
  NoteStakeholder, NoteStakeholderInsert,
  ViewFilter, ViewSort,
  ActivityLogEntry, ActivityType, TemplateConfig, } from '@/lib/entities/types';
import { resolveEntitySource } from '@/lib/internal-tools/entitySourceResolver';
import { resolveInstanceSource } from '@/lib/entities/instanceResolver';
import { fetchEffectiveFieldRows, groupFieldUsage } from '@/lib/entities/effectiveFieldRefs';
import { fireberryAccountToNoteRecord } from '@/lib/crm/adapters/fireberryAccountToNoteRecord';
import { fireberryRowToNoteRecord } from '@/lib/crm/adapters/fireberryRowToNoteRecord';
import { marketplaceRowToNoteRecord } from '@/lib/crm/adapters/marketplaceRowToNoteRecord';
import type { FbAccountRow } from '@/lib/crm/adapters/fbAccountToCompany';

// ─── Field Key Helpers ──────────────────────────────

/** Resolve a meta_key or alias to the canonical GlobalField */
export function resolveField(fields: GlobalField[], key: string): GlobalField | undefined {
  return fields.find(f => f.meta_key === key || f.aliases.includes(key));
}

/** Generate a URL-safe meta_key from a label string */
export function generateMetaKey(label: string): string {
  const slug = label
    .normalize('NFKD')
    .replace(/[\u0590-\u05FF]+/g, m => transliterateHebrew(m))
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s-]+/g, '_')
    .toLowerCase()
    .slice(0, 40);
  const shortId = Math.random().toString(36).slice(2, 6);
  return slug ? `${slug}_${shortId}` : `field_${shortId}`;
}

/** Basic Hebrew transliteration for meta_key generation */
function transliterateHebrew(text: string): string {
  const map: Record<string, string> = {
    'א': 'a', 'ב': 'b', 'ג': 'g', 'ד': 'd', 'ה': 'h', 'ו': 'v', 'ז': 'z',
    'ח': 'ch', 'ט': 't', 'י': 'y', 'כ': 'k', 'ך': 'k', 'ל': 'l', 'מ': 'm',
    'ם': 'm', 'נ': 'n', 'ן': 'n', 'ס': 's', 'ע': 'a', 'פ': 'p', 'ף': 'p',
    'צ': 'ts', 'ץ': 'ts', 'ק': 'q', 'ר': 'r', 'ש': 'sh', 'ת': 't',
  };
  return text.split('').map(c => map[c] ?? '').join('');
}

/** Merge field A into field B: A's meta_key becomes an alias on B, A is deleted */
export async function mergeField(
  sourceId: string,
  targetId: string,
  fields: GlobalField[],
): Promise<boolean> {
  const source = fields.find(f => f.id === sourceId);
  const target = fields.find(f => f.id === targetId);
  if (!source || !target) return false;

  // Add source's meta_key + its aliases to target's aliases
  const newAliases = [...new Set([
    ...target.aliases,
    source.meta_key,
    ...source.aliases,
  ])];

  const ok = await updateGlobalField(targetId, { aliases: newAliases });
  if (!ok) return false;

  // Delete the source field
  await deleteGlobalField(sourceId);
  return true;
}

// ─── Global Fields ──────────────────────────────────

export async function fetchGlobalFields(): Promise<GlobalField[]> {
  const { data, error } = await supabase
    .from('global_fields')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) { console.error('fetchGlobalFields:', error.message); return []; }
  return (data ?? []) as GlobalField[];
}

export async function createGlobalField(field: GlobalFieldInsert): Promise<GlobalField | null> {
  const { data, error } = await supabase
    .from('global_fields')
    .insert([field])
    .select()
    .single();
  if (error) { console.error('createGlobalField:', error.message); return null; }
  return data as GlobalField;
}

export async function updateGlobalField(id: string, patch: Partial<GlobalFieldInsert>): Promise<boolean> {
  const { error } = await supabase
    .from('global_fields')
    .update(patch)
    .eq('id', id);
  if (error) { console.error('updateGlobalField:', error.message); return false; }
  return true;
}

export async function deleteGlobalField(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('global_fields')
    .delete()
    .eq('id', id);
  if (error) { console.error('deleteGlobalField:', error.message); return false; }
  return true;
}

// ─── Field Groups ───────────────────────────────────

export async function fetchFieldGroups(): Promise<FieldGroup[]> {
  const { data, error } = await supabase
    .from('field_groups')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) { console.error('fetchFieldGroups:', error.message); return []; }
  return (data ?? []) as FieldGroup[];
}

export async function createFieldGroup(group: FieldGroupInsert): Promise<FieldGroup | null> {
  const { data, error } = await supabase
    .from('field_groups')
    .insert([group])
    .select()
    .single();
  if (error) { console.error('createFieldGroup:', error.message); return null; }
  return data as FieldGroup;
}

export async function updateFieldGroup(id: string, patch: Partial<FieldGroupInsert>): Promise<boolean> {
  const { error } = await supabase
    .from('field_groups')
    .update(patch)
    .eq('id', id);
  if (error) { console.error('updateFieldGroup:', error.message); return false; }
  return true;
}

// ─── Entity Types ───────────────────────────────────

export async function fetchEntityTypes(includeDev = false): Promise<EntityType[]> {
  let query = supabase
    .from('entity_types')
    .select('*')
    .order('sort_order', { ascending: true });
  // Private dev records have a dedicated store and count, even for their owner.
  if (!includeDev) query = query.neq('slug', 'dev');
  const { data, error } = await query;
  if (error) { console.error('fetchEntityTypes:', error.message); return []; }
  return ((data ?? []) as EntityType[]).map(withTemplateLayout);
}

/**
 * A stored template_config may carry only notes or decision refs, with no
 * layout (the 15 universal-hierarchy types of 18.09 do). Every lens reads
 * template_config.layout.sections, so fill the layout branch alone and leave
 * the rest as stored — available_views stays absent, not [].
 */
export function withTemplateLayout(et: EntityType): EntityType {
  const tc = et.template_config as (Partial<TemplateConfig> & Record<string, unknown>) | null;
  if (!tc || typeof tc !== 'object') return et;
  const layout = (tc.layout ?? {}) as Partial<TemplateConfig['layout']>;
  if (Array.isArray(layout.sections) && Array.isArray(layout.field_order) && layout.meta_columns) return et;
  return {
    ...et,
    template_config: {
      ...tc,
      layout: {
        meta_columns: layout.meta_columns ?? 2,
        field_order: Array.isArray(layout.field_order) ? layout.field_order : [],
        sections: Array.isArray(layout.sections) ? layout.sections : [],
      },
    } as TemplateConfig,
  };
}

/**
 * Effective field_refs for an entity type — its own fields PLUS every field
 * inherited from ancestor libraries via entity_types.parent_id (DEC-0017).
 * Reads v_entity_type_effective_fields, which already flattens the ancestry
 * (depth 0 = the type's own, depth 1+ = inherited). Falls back to the flat
 * field_refs on the row if the view is unavailable, so a type with no parent
 * behaves exactly as before.
 */
export async function fetchEffectiveFieldRefs(
  slug: string,
): Promise<{ ref: string; from_layer: string; depth: number }[]> {
  const { data, error } = await supabase
    .from('v_entity_type_effective_fields')
    .select('field_ref, from_layer, depth')
    .eq('slug', slug)
    .order('depth', { ascending: true });
  if (error) { console.error('fetchEffectiveFieldRefs:', error.message); return []; }
  return (data ?? []).map((r) => ({
    ref: (r as { field_ref: string }).field_ref,
    from_layer: (r as { from_layer: string }).from_layer,
    depth: (r as { depth: number }).depth,
  }));
}

export async function createEntityType(type: EntityTypeInsert): Promise<EntityType | null> {
  const { data, error } = await supabase
    .from('entity_types')
    .insert([type])
    .select()
    .single();
  if (error) { console.error('createEntityType:', error.message); return null; }
  return data as EntityType;
}

export async function updateEntityType(id: string, patch: Partial<EntityTypeInsert>): Promise<boolean> {
  const { error } = await supabase
    .from('entity_types')
    .update(patch)
    .eq('id', id);
  if (error) { console.error('updateEntityType:', error.message); return false; }
  return true;
}

export async function deleteEntityType(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('entity_types')
    .delete()
    .eq('id', id);
  if (error) { console.error('deleteEntityType:', error.message); return false; }
  return true;
}

// ─── Entity Connections ─────────────────────────────

export async function fetchEntityConnections(): Promise<EntityConnection[]> {
  const { data, error } = await supabase
    .from('entity_connections')
    .select('*');
  if (error) { console.error('fetchEntityConnections:', error.message); return []; }
  return (data ?? []) as EntityConnection[];
}

export async function createEntityConnection(conn: EntityConnectionInsert): Promise<EntityConnection | null> {
  const { data, error } = await supabase
    .from('entity_connections')
    .insert([conn])
    .select()
    .single();
  if (error) { console.error('createEntityConnection:', error.message); return null; }
  return data as EntityConnection;
}

export async function deleteEntityConnection(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('entity_connections')
    .delete()
    .eq('id', id);
  if (error) { console.error('deleteEntityConnection:', error.message); return false; }
  return true;
}

// ─── Single Note ────────────────────────────────────

/**
 * ⚠️ `entityType` נוסף 09.08 — בלעדיו אי אפשר לפתוח מופע שאינו ב-vb_records.
 *
 * הבאג שגל דיווח עליו: *"בתי עסק כן טוען אבל לא ניתן להיכנס לתוך המופע."*
 * הסיבה מדויקת — `fetchNotes()` קיבל ענף מראה ב-01.08, אבל `fetchNote()`
 * **לא**. הרשימה נטענה מ-`fb_records` ולחיצה על שורה חיפשה את אותו מזהה
 * ב-`vb_records`, שם הוא לא קיים.
 *
 * ולמה זה לא נתפס אז: החתימה קיבלה `id` בלבד, ולכן הפונקציה **לא יכלה
 * לדעת** באיזו טבלה לחפש גם אילו רצתה. הפרמטר אופציונלי — כל 5 הקוראים
 * הקיימים ממשיכים לעבוד בדיוק כשעבדו.
 */
export async function fetchNote(id: string, entityType?: string): Promise<NoteRecord | null> {
  const source = entityType ? resolveEntitySource(entityType) : null;

  if (source?.origin === 'fireberry-mirror' && source.table) {
    const { data, error } = await supabase
      .from(source.table)
      .select('*')
      .eq(source.idColumn ?? 'id', id)
      .maybeSingle();
    if (error) { console.error('fetchNote (fireberry-mirror):', error.message); return null; }
    if (!data) return null;
    // אותה הבחנה כמו ברשימה — מופע בודד חייב לחזור עם הישות שלו ולא עם
    // `fireberry_account` קבוע, אחרת מסך המופע פותח את הישות הלא נכונה.
    return source.slug === 'fireberry_account'
      ? fireberryAccountToNoteRecord(data as FbAccountRow)
      : fireberryRowToNoteRecord(data as Record<string, unknown>, source);
  }

  if (source?.origin === 'marketplace' && source.table && source.table !== 'vb_records') {
    // שורה שנמחקה רכות אינה נפתחת כמופע — אותו סינון כמו ברשימה.
    let q = supabase
      .from(source.table)
      .select('*')
      .eq(source.idColumn ?? 'id', id);
    if (source.deletedColumn) q = q.is(source.deletedColumn, null);
    const { data, error } = await q.maybeSingle();
    if (error) { console.error(`fetchNote (marketplace/${source.table}):`, error.message); return null; }
    return data ? marketplaceRowToNoteRecord(data as Record<string, unknown>, source) : null;
  }

  const { data, error } = await supabase
    .from('vb_records')
    .select('*')
    .eq('id', id)
    .eq('is_deleted', false)
    .single();
  if (error) { console.error('fetchNote:', error.message); return null; }
  return data as NoteRecord;
}

/**
 * True when the stored row is a local, writable vb_records record. Judged by
 * the row's own entity_type, read by the writer under RLS — never by the
 * caller's slug or the URL. Same rule the screen and the adapter use (Rule 10).
 */
function isWritableVbRecord(row: { id: string; meta: unknown; entity_type: string | null }): boolean {
  const source = resolveInstanceSource(
    row as unknown as NoteRecord,
    row.entity_type ? resolveEntitySource(row.entity_type) : null,
  );
  return source.writable && source.table === 'vb_records';
}

export async function updateNoteTitle(id: string, title: string): Promise<boolean> {
  const { data: current, error: fetchErr } = await supabase
    .from('vb_records')
    .select('id, meta, entity_type')
    .eq('id', id)
    .eq('is_deleted', false)
    .single();
  if (fetchErr || !current) return false;
  if (!isWritableVbRecord(current)) { console.warn('updateNoteTitle: source is not writable'); return false; }

  const { data: updated, error } = await supabase
    .from('vb_records')
    .update({ title, last_edited_at: new Date().toISOString() })
    .eq('id', id)
    .eq('is_deleted', false)
    .select('id');
  if (error) { console.error('updateNoteTitle:', error.message); return false; }
  // Deleted between the read and the write, or RLS — both are zero rows, not an error.
  if (!updated?.length) { console.warn('updateNoteTitle: no row matched'); return false; }
  return true;
}

// ─── Notes (vb_records) ─────────────────────────────

export async function fetchNotes(
  entityType?: string,
  filters: ViewFilter[] = [],
  sort?: ViewSort,
  page = 0,
  pageSize = 50,
  fields: Pick<GlobalField, 'meta_key' | 'field_type'>[] = [],
): Promise<{ data: NoteRecord[]; count: number }> {
  // fireberry-mirror branch (2026-08-01) — the gap verified live the same
  // day: entitySourceResolver.ts already declares slugs like
  // `fireberry_account` as `status: "ready"`, but this function only ever
  // read `vb_records`, so every one of the 8 views got an empty list for
  // them. This is the missing hop, not a new engine — every view downstream
  // still only sees a NoteRecord and doesn't know or care that it came from
  // fb_records. Filters/sort on jsonb meta paths are NOT translated here yet
  // (fb_records' data column, not vb_records' meta) — basic pagination +
  // default sort only; full parity is separate follow-up work, flagged, not
  // silently pretended to be complete.
  const mirrorSource = entityType ? resolveEntitySource(entityType) : null;
  if (mirrorSource?.origin === 'fireberry-mirror' && mirrorSource.table && mirrorSource.filterColumn && mirrorSource.entityTypeFilter) {
    const from = page * pageSize;
    const { data, error, count } = await supabase
      .from(mirrorSource.table)
      .select('*', { count: 'exact' })
      .eq(mirrorSource.filterColumn, mirrorSource.entityTypeFilter)
      .order('modified_on', { ascending: false, nullsFirst: false })
      .range(from, from + pageSize - 1);

    if (error) { console.error('fetchNotes (fireberry-mirror):', error.message); return { data: [], count: 0 }; }
    // ⚠️ הממפה הייעודי מקודד בית עסק — `entity_type` קבוע ורשימת שדות של
    // חשבון. הוא נשאר עבור `fireberry_account` בלבד, שנבדק חי ועובד; כל
    // שאר עדשות המראה עוברות בממפה הגנרי, אחרת הן מציגות שורות מסומנות
    // כבית עסק ובלי השדות שלהן. פירוט מלא: fireberryRowToNoteRecord.ts
    return {
      data:
        mirrorSource.slug === 'fireberry_account'
          ? ((data ?? []) as FbAccountRow[]).map(fireberryAccountToNoteRecord)
          : ((data ?? []) as Record<string, unknown>[]).map((r) =>
              fireberryRowToNoteRecord(r, mirrorSource),
            ),
      count: count ?? 0,
    };
  }

  // ⚠️ ענף הטבלה הייעודית (09.08) — התיקון לבאג שגל תפס תוך שעה.
  //
  // שמונה ישויות נרשמו באותו יום עם `origin: "marketplace"` מעל טבלאות
  // אמיתיות (skills, pm_team, comm_messages ועוד). הרישום היה נכון, אבל
  // כאן היו **שני ענפים בלבד** — fireberry-mirror ו-vb_records — ולכן
  // כולן נפלו ל-vb_records, שבו אין להן אף שורה, והציגו רשימה ריקה.
  // 145 הודעות ו-66 סיבות סגירה היו שם כל הזמן.
  //
  // הענף גנרי ונגזר מתצורת ה-resolver — עדשה תשיעית לא תדרוש קוד חדש.
  if (
    mirrorSource?.origin === 'marketplace' &&
    mirrorSource.table &&
    mirrorSource.table !== 'vb_records'
  ) {
    const from = page * pageSize;
    let q = supabase.from(mirrorSource.table).select('*', { count: 'exact' });
    // סינון לפי סוג רק כשהעדשה מצהירה עליו (כמה slugs על טבלה אחת).
    if (mirrorSource.filterColumn && mirrorSource.entityTypeFilter) {
      q = q.eq(mirrorSource.filterColumn, mirrorSource.entityTypeFilter);
    }
    // ⚠️ מחיקה רכה (18.09) — pm_tasks מחזיקה 31 משימות עם deleted_at,
    // והענף הזה הציג אותן ברשימה ובספירה. העמודה מוצהרת ב-resolver ולא
    // מנוחשת כאן: סינון על עמודה שאינה קיימת מפיל את כל השאילתה.
    if (mirrorSource.deletedColumn) {
      q = q.is(mirrorSource.deletedColumn, null);
    }
    const { data, error, count } = await q.range(from, from + pageSize - 1);
    if (error) {
      console.error(`fetchNotes (marketplace/${mirrorSource.table}):`, error.message);
      return { data: [], count: 0 };
    }
    return {
      data: (data ?? []).map((r) =>
        marketplaceRowToNoteRecord(r as Record<string, unknown>, mirrorSource),
      ),
      count: count ?? 0,
    };
  }

  let query = supabase
    .from('vb_records')
    .select('*', { count: 'exact' })
    .eq('is_deleted', false);

  if (entityType) {
    query = query.eq('entity_type', entityType);
  }

  // Apply meta filters
  for (const f of filters) {
    const path = `meta->>${f.field}`;
    switch (f.operator) {
      case 'eq': query = query.eq(path, f.value); break;
      case 'neq': query = query.neq(path, f.value); break;
      case 'contains': query = query.ilike(path, `%${f.value}%`); break;
      case 'gt': query = query.gt(path, f.value); break;
      case 'lt': query = query.lt(path, f.value); break;
      case 'is_empty': query = query.is(path, null); break;
      case 'is_not_empty': query = query.not(path, 'is', null); break;
    }
  }

  // Sort
  if (sort) {
    if (sort.field === 'title' || sort.field === 'created_at' || sort.field === 'last_edited_at') {
      query = query.order(sort.field, { ascending: sort.direction === 'asc' });
    } else {
      // ->> turns 2 and 10 into text. Keep JSON numeric values typed so the
      // database orders the entire result correctly BEFORE applying the page.
      const field = fields.find(f => f.meta_key === sort.field);
      const numeric = field && ['number', 'currency', 'rating'].includes(field.field_type);
      query = query.order(`meta${numeric ? '->' : '->>'}${sort.field}` as string,
        { ascending: sort.direction === 'asc', nullsFirst: false });
    }
  } else {
    query = query.order('last_edited_at', { ascending: false });
  }

  // Pagination
  query = query.order('id', { ascending: true });
  const from = page * pageSize;
  query = query.range(from, from + pageSize - 1);

  const { data, error, count } = await query;
  if (error) { console.error('fetchNotes:', error.message); return { data: [], count: 0 }; }
  return { data: (data ?? []) as NoteRecord[], count: count ?? 0 };
}

export async function createNote(
  title: string,
  entityType?: string,
  meta: Record<string, unknown> = {},
): Promise<NoteRecord | null> {
  const context = await resolveRecordWriteContext(supabase, { entityType });
  if (!context) return null;

  const { data, error } = await supabase
    .from('vb_records')
    .insert([{
      id: context.recordId,
      title,
      entity_type: entityType ?? null,
      meta,
      record_type: entityType ?? 'note',
      status: 'active',
      source: 'manual',
      workspace_id: context.workspaceId,
      entity_id: context.entityId,
      ...(context.entityTypeId
        ? { entity_type_id: context.entityTypeId }
        : {}),
      created_by: context.userId,
    }])
    .select()
    .single();
  if (error) { console.error('createNote:', error.message); return null; }
  return data as NoteRecord;
}

export async function updateNoteMeta(
  id: string,
  metaPatch: Record<string, unknown>,
  options?: { trackActivity?: boolean; actorId?: string | null },
): Promise<boolean> {
  // Merge meta — fetch current, merge, update
  const { data: current, error: fetchErr } = await supabase
    .from('vb_records')
    .select('meta, entity_type')
    .eq('id', id)
    .single();
  if (fetchErr || !current) return false;

  const oldMeta = (current.meta as Record<string, unknown>) ?? {};
  const merged = { ...oldMeta, ...metaPatch };
  const { error } = await supabase
    .from('vb_records')
    .update({ meta: merged, last_edited_at: new Date().toISOString() })
    .eq('id', id);
  if (error) { console.error('updateNoteMeta:', error.message); return false; }

  // Auto-log field changes when track_activity is enabled
  if (options?.trackActivity) {
    for (const [key, newVal] of Object.entries(metaPatch)) {
      const oldVal = oldMeta[key];
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        const activityType: ActivityType = key === 'status' ? 'status_change' : 'field_change';
        await logActivity(id, activityType, {
          actorId: options.actorId ?? null,
          fieldKey: key,
          oldValue: oldVal != null ? String(oldVal) : null,
          newValue: newVal != null ? String(newVal) : null,
        });
      }
    }
  }

  return true;
}

export async function updateNoteEntityType(id: string, entityType: string | null): Promise<boolean> {
  const { error } = await supabase
    .from('vb_records')
    .update({ entity_type: entityType })
    .eq('id', id);
  if (error) { console.error('updateNoteEntityType:', error.message); return false; }
  return true;
}

export async function deleteNote(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('vb_records')
    .update({ is_deleted: true })
    .eq('id', id);
  if (error) { console.error('deleteNote:', error.message); return false; }
  return true;
}

// ─── Batch Note Info ────────────────────────────────
// Lightweight fetch for enriching links (title + entity_type only)

export async function fetchNoteInfoBatch(ids: string[]): Promise<Record<string, { title: string; entity_type: string | null }>> {
  if (ids.length === 0) return {};
  const { data, error } = await supabase
    .from('vb_records')
    .select('id, title, entity_type')
    .in('id', ids);
  if (error) { console.error('fetchNoteInfoBatch:', error.message); return {}; }
  const map: Record<string, { title: string; entity_type: string | null }> = {};
  for (const row of data ?? []) {
    map[row.id] = { title: row.title, entity_type: row.entity_type };
  }
  return map;
}

// ─── Note Relations ─────────────────────────────────

export async function fetchNoteRelations(noteId: string): Promise<NoteRelation[]> {
  const { data, error } = await supabase
    .from('note_relations')
    .select('*')
    .or(`source_id.eq.${noteId},target_id.eq.${noteId}`);
  if (error) { console.error('fetchNoteRelations:', error.message); return []; }
  return (data ?? []) as NoteRelation[];
}

export async function createNoteRelation(
  sourceId: string,
  targetId: string,
  relationType = 'related',
): Promise<NoteRelation | null> {
  if (/^[0-9a-f-]{36}$/i.test(relationType)) {
    const connection = await supabase.from('entity_connections').select('source_type').eq('id', relationType).maybeSingle();
    if (connection.data?.source_type === 'diagram') {
      const result = await supabase.rpc('diagram_relation_command', {p_action:'link',p_source_id:sourceId,p_target_id:targetId,p_relation_id:null});
      return result.error ? null : result.data as NoteRelation;
    }
  }
  const { data, error } = await supabase
    .from('note_relations')
    .insert([{ source_id: sourceId, target_id: targetId, relation_type: relationType }])
    .select()
    .single();
  if (error) { console.error('createNoteRelation:', error.message); return null; }
  return data as NoteRelation;
}

export async function deleteNoteRelation(id: string): Promise<boolean> {
  const row = await supabase.from('note_relations').select('*').eq('id',id).maybeSingle();
  if (row.data?.diagram_owned) {
    const result = await supabase.rpc('diagram_relation_command',{p_action:'unlink',p_source_id:null,p_target_id:null,p_relation_id:id});
    return !result.error;
  }
  const { error } = await supabase
    .from('note_relations')
    .delete()
    .eq('id', id);
  if (error) { console.error('deleteNoteRelation:', error.message); return false; }
  return true;
}

// ─── Cross-Entity Search ────────────────────────────
// Search a meta_key value across ALL entity types.
// e.g., search phone "050-1234567" → finds in clients, contacts, etc.

export async function searchByMetaKey(
  metaKey: string,
  value: string,
  allFields?: GlobalField[],
): Promise<NoteRecord[]> {
  // If the key is an alias, resolve to canonical meta_key
  const resolvedKey = allFields ? (resolveField(allFields, metaKey)?.meta_key ?? metaKey) : metaKey;

  const { data, error } = await supabase
    .from('vb_records')
    .select('*')
    .eq('is_deleted', false)
    .ilike(`meta->>${resolvedKey}`, `%${value}%`)
    .order('last_edited_at', { ascending: false })
    .limit(50);
  if (error) { console.error('searchByMetaKey:', error.message); return []; }
  return (data ?? []) as NoteRecord[];
}

// Full-text search across title + meta values
export async function searchNotes(
  query: string,
  entityType?: string,
): Promise<NoteRecord[]> {
  let q = supabase
    .from('vb_records')
    .select('*')
    .eq('is_deleted', false)
    .ilike('title', `%${query}%`);

  if (entityType) {
    q = q.eq('entity_type', entityType);
  }

  q = q.order('last_edited_at', { ascending: false }).limit(50);

  const { data, error } = await q;
  if (error) { console.error('searchNotes:', error.message); return []; }
  return (data ?? []) as NoteRecord[];
}

// ─── Bulk Operations ────────────────────────────────

export async function bulkUpdateMeta(
  ids: string[],
  metaPatch: Record<string, unknown>,
  options?: { trackActivity?: boolean; actorId?: string | null },
): Promise<{ success: boolean; updatedCount: number }> {
  let updatedCount = 0;
  for (const id of ids) {
    const ok = await updateNoteMeta(id, metaPatch, options);
    if (ok) updatedCount++;
  }
  return { success: updatedCount === ids.length, updatedCount };
}

// ─── Field Usage Across Entities ────────────────────
// Which entity types use each global field — directly or by inheritance
// (DEC-0409 criterion 1). One read of v_entity_type_effective_fields for all
// fields; the flat field_refs is only the fallback when the view is unavailable.

export async function fetchFieldUsageMap(): Promise<Record<string, string[]>> {
  const rows = await fetchEffectiveFieldRows(supabase);
  if (rows) return groupFieldUsage(rows);
  const types = await fetchEntityTypes();
  const usage: Record<string, string[]> = {};
  for (const t of types) {
    for (const ref of t.field_refs ?? []) (usage[ref] ??= []).push(t.slug);
  }
  return usage;
}

// ─── Stakeholders ───────────────────────────────────
// Stakeholders are people connected to a note/deal with specific roles,
// access levels, and notification preferences.

export async function fetchStakeholders(noteId: string): Promise<NoteStakeholder[]> {
  const { data, error } = await supabase
    .from('note_stakeholders')
    .select('*')
    .eq('note_id', noteId)
    .order('is_primary', { ascending: false });

  if (error) { console.error('fetchStakeholders:', error.message); return []; }

  const stakeholders = (data ?? []) as NoteStakeholder[];

  // Enrich with contact info
  if (stakeholders.length > 0) {
    const contactIds = stakeholders.map(s => s.contact_note_id);
    const { data: contacts } = await supabase
      .from('vb_records')
      .select('id, title, meta, entity_type')
      .in('id', contactIds);

    if (contacts) {
      const contactMap = new Map(contacts.map(c => [c.id, c]));
      for (const s of stakeholders) {
        const contact = contactMap.get(s.contact_note_id);
        if (contact) {
          s.contact_title = contact.title;
          s.contact_meta = (contact.meta as Record<string, unknown>) ?? {};
          s.contact_entity_type = contact.entity_type;
        }
      }
    }
  }

  return stakeholders;
}

export async function addStakeholder(insert: NoteStakeholderInsert): Promise<NoteStakeholder | null> {
  const { data, error } = await supabase
    .from('note_stakeholders')
    .insert([insert])
    .select()
    .single();
  if (error) { console.error('addStakeholder:', error.message); return null; }
  return data as NoteStakeholder;
}

export async function updateStakeholder(
  id: string,
  patch: Partial<NoteStakeholderInsert>,
): Promise<boolean> {
  const { error } = await supabase
    .from('note_stakeholders')
    .update(patch)
    .eq('id', id);
  if (error) { console.error('updateStakeholder:', error.message); return false; }
  return true;
}

export async function removeStakeholder(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('note_stakeholders')
    .delete()
    .eq('id', id);
  if (error) { console.error('removeStakeholder:', error.message); return false; }
  return true;
}

// Get all notes where a contact is a stakeholder
export async function fetchStakeholderDeals(contactNoteId: string): Promise<{
  noteId: string;
  role: string;
  accessLevel: string;
}[]> {
  const { data, error } = await supabase
    .from('note_stakeholders')
    .select('note_id, role, access_level')
    .eq('contact_note_id', contactNoteId);
  if (error) { console.error('fetchStakeholderDeals:', error.message); return []; }
  return (data ?? []).map(d => ({
    noteId: d.note_id,
    role: d.role,
    accessLevel: d.access_level,
  }));
}

// ─── Activity Log ──────────────────────────────────

export async function fetchActivityLog(
  noteId: string,
  limit = 50,
): Promise<ActivityLogEntry[]> {
  const { data, error } = await supabase
    .from('note_activity_log')
    .select('*')
    .eq('note_id', noteId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) { console.error('fetchActivityLog:', error.message); return []; }
  return (data ?? []) as ActivityLogEntry[];
}

/**
 * Merged activity feed across MULTIPLE records — the base for the entity
 * engine's `stream` view (Sprint B Task 8). Sibling of `fetchActivityLog`
 * above, not a modification of it — that function has other callers
 * (ActivityFeed.tsx) scoped to one record; this is additive.
 *
 * This is the safe common denominator of Gal's "spine of what happens"
 * description: one merged, reverse-chronological timeline of every logged
 * action across the records currently visible in a view. It does NOT yet
 * follow cross-entity relations (lead -> contact -> business -> deal) the
 * way Gal described — that needs `note_relations`/`vb_record_relations`
 * data that isn't populated yet, and its own design pass. This is the
 * per-visible-set activity timeline; the cross-entity chain is a distinct,
 * larger feature layered on top later.
 */
export async function fetchActivityLogForNotes(
  noteIds: string[],
  limit = 100,
): Promise<ActivityLogEntry[]> {
  if (noteIds.length === 0) return [];
  const { data, error } = await supabase
    .from('note_activity_log')
    .select('*')
    .in('note_id', noteIds)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) { console.error('fetchActivityLogForNotes:', error.message); return []; }
  return (data ?? []) as ActivityLogEntry[];
}

export async function logActivity(
  noteId: string,
  activityType: ActivityType,
  opts: {
    actorId?: string | null;
    fieldKey?: string | null;
    oldValue?: string | null;
    newValue?: string | null;
    metadata?: Record<string, unknown> | null;
    noteText?: string | null;
  } = {},
): Promise<boolean> {
  const { error } = await supabase
    .from('note_activity_log')
    .insert([{
      note_id: noteId,
      // No explicit null: the column defaults to auth.uid() (finding מ5).
      ...(opts.actorId ? { actor_id: opts.actorId } : {}),
      activity_type: activityType,
      field_key: opts.fieldKey ?? null,
      old_value: opts.oldValue ?? null,
      new_value: opts.newValue ?? null,
      metadata: opts.metadata ?? null,
      note_text: opts.noteText ?? null,
    }]);
  if (error) { console.error('logActivity:', error.message); return false; }
  return true;
}

export async function addComment(
  noteId: string,
  text: string,
  actorId?: string | null,
): Promise<boolean> {
  return logActivity(noteId, 'comment', { actorId, noteText: text });
}

export async function addCallLogEntry(
  noteId: string,
  summary: string,
  metadata?: Record<string, unknown>,
  actorId?: string | null,
): Promise<boolean> {
  return logActivity(noteId, 'call_log', {
    actorId,
    noteText: summary,
    metadata,
  });
}

// ─── Template Linking ──────────────────────────────
// Templates are vb_records with record_type = 'template' or 'document'.
// Link them to entity instances via note_relations with relation_type = 'template'.

export async function fetchLinkedTemplates(noteId: string): Promise<NoteRecord[]> {
  // Get relations where this note is the source and relation_type = 'template'
  const { data: rels, error } = await supabase
    .from('note_relations')
    .select('target_id')
    .eq('source_id', noteId)
    .eq('relation_type', 'template');
  if (error || !rels?.length) return [];

  const templateIds = rels.map(r => r.target_id);
  const { data: templates } = await supabase
    .from('vb_records')
    .select('*')
    .in('id', templateIds)
    .eq('is_deleted', false)
    .order('last_edited_at', { ascending: false });

  return (templates ?? []) as NoteRecord[];
}

export async function linkTemplate(noteId: string, templateId: string): Promise<boolean> {
  const { error } = await supabase
    .from('note_relations')
    .insert([{ source_id: noteId, target_id: templateId, relation_type: 'template' }]);
  if (error) { console.error('linkTemplate:', error.message); return false; }
  return true;
}

export async function unlinkTemplate(noteId: string, templateId: string): Promise<boolean> {
  const { error } = await supabase
    .from('note_relations')
    .delete()
    .eq('source_id', noteId)
    .eq('target_id', templateId)
    .eq('relation_type', 'template');
  if (error) { console.error('unlinkTemplate:', error.message); return false; }
  return true;
}

export async function searchTemplates(query: string): Promise<NoteRecord[]> {
  const { data, error } = await supabase
    .from('vb_records')
    .select('*')
    .eq('is_deleted', false)
    .in('record_type', ['template', 'document'])
    .ilike('title', `%${query}%`)
    .order('last_edited_at', { ascending: false })
    .limit(20);
  if (error) { console.error('searchTemplates:', error.message); return []; }
  return (data ?? []) as NoteRecord[];
}

export async function createTemplate(title: string): Promise<NoteRecord | null> {
  const { data, error } = await supabase
    .from('vb_records')
    .insert([{
      title,
      record_type: 'template',
      status: 'active',
      source: 'manual',
      content: { type: 'doc', content: [{ type: 'paragraph' }] },
      meta: {},
    }])
    .select()
    .single();
  if (error) { console.error('createTemplate:', error.message); return null; }
  return data as NoteRecord;
}

// ─── Active / Inactive Toggle ──────────────────────
//
// Both writers are conditional updates (D-6, approved by Gal 11.10.2026).
// The update only lands if last_edited_at still equals the version the
// caller passes — the one it showed the user. The writer never falls back to
// a version it read itself: that read would be newer than the screen and
// would hide exactly the conflict the guard exists to catch (fix plan §3).
// Pass the string as read from the database; Postgres compares it as
// timestamptz, so the microseconds must survive the round trip.
// Two BEFORE UPDATE triggers on vb_records bump last_edited_at on every
// write, so any write in between moves the version and the guard misses.
// A missed guard returns false and logs no activity.
//
// Both also refuse a deleted row, and a row whose stored entity_type resolves
// to a non-writable source. A repeat call is a no-op that returns true, so a
// second deactivate never overwrites last_active_status with 'inactive'.

/** Runs the guarded update. True only when exactly the expected version was written. */
async function updateIfVersion(
  id: string,
  version: string | null,
  patch: Record<string, unknown>,
  label: string,
): Promise<boolean> {
  // is_deleted in the update too — a row deleted after the read matches zero rows.
  let update = supabase.from('vb_records').update(patch).eq('id', id).eq('is_deleted', false);
  update = version === null ? update.is('last_edited_at', null) : update.eq('last_edited_at', version);
  const { data: updated, error } = await update.select('id');
  if (error) { console.error(`${label}:`, error.message); return false; }
  // RLS or a concurrent write both surface as zero rows, never as an error.
  if (!updated?.length) { console.warn(`${label}: no row matched the expected version`); return false; }
  return true;
}

export async function deactivateNote(
  id: string,
  actorId: string | null,
  expectedLastEditedAt: string | null,
): Promise<boolean> {
  // Save current status as last_active_status before deactivating
  const { data: current, error: fetchErr } = await supabase
    .from('vb_records')
    .select('id, meta, status, last_edited_at, entity_type')
    .eq('id', id)
    .eq('is_deleted', false)
    .single();
  if (fetchErr || !current) return false;
  if (!isWritableVbRecord(current)) { console.warn('deactivateNote: source is not writable'); return false; }
  // Already inactive — writing again would store 'inactive' as last_active_status.
  // Not a .neq() in the update: NULL <> 'inactive' is not true, so null-status rows would be blocked.
  if (current.status === 'inactive') return true;

  const meta = (current.meta as Record<string, unknown>) ?? {};
  const written = await updateIfVersion(id, expectedLastEditedAt, {
    status: 'inactive',
    meta: { ...meta, last_active_status: current.status },
    last_edited_at: new Date().toISOString(),
  }, 'deactivateNote');
  if (!written) return false;

  await logActivity(id, 'deactivated', {
    actorId,
    oldValue: current.status,
    newValue: 'inactive',
  });
  return true;
}

export async function reactivateNote(
  id: string,
  actorId: string | null,
  expectedLastEditedAt: string | null,
): Promise<boolean> {
  // Restore last_active_status
  const { data: current, error: fetchErr } = await supabase
    .from('vb_records')
    .select('id, meta, status, last_edited_at, entity_type')
    .eq('id', id)
    .eq('is_deleted', false)
    .single();
  if (fetchErr || !current) return false;
  if (!isWritableVbRecord(current)) { console.warn('reactivateNote: source is not writable'); return false; }
  // Not inactive — there is nothing to restore, and writing would overwrite the live status.
  if (current.status !== 'inactive') return true;

  const meta = (current.meta as Record<string, unknown>) ?? {};
  const restoredStatus = (meta.last_active_status as string) || 'active';
  const { last_active_status: _lastStatus, ...cleanMeta } = meta;

  const written = await updateIfVersion(id, expectedLastEditedAt, {
    status: restoredStatus,
    meta: cleanMeta,
    last_edited_at: new Date().toISOString(),
  }, 'reactivateNote');
  if (!written) return false;

  await logActivity(id, 'reactivated', {
    actorId,
    oldValue: 'inactive',
    newValue: restoredStatus,
  });
  return true;
}
